import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { approveRequest, rejectRequest } from 'store/approvalsSlice';
import { putOnHoldThunk, resumeRequestThunk } from 'store/allRequestsSlice';
import { AssetRequest } from 'api/assetsAPI';
import { ColumnDef } from '@tanstack/react-table';
import AdvanceTable from 'components/base/AdvanceTable';
import AdvanceTableFooter from 'components/base/AdvanceTableFooter';
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import FeatherIcon from 'feather-icons-react';
import { Badge, Button, Modal, Dropdown } from 'react-bootstrap';
import PhoenixLoader from 'components/common/PhoenixLoader';
import PutOnHoldModal from 'components/modals/PutOnHoldModal';
import RevealDropdown, {
  RevealDropdownTrigger
} from 'components/base/RevealDropdown';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBuilding,
  faLaptop,
  faUser,
  faTag,
  faExclamationCircle,
  faCheckCircle,
  faCalendar
} from '@fortawesome/free-solid-svg-icons';

import { AppDispatch, RootState } from 'store';

const statusColors: Record<string, string> = {
  pending: 'warning',
  approved: 'success',
  rejected: 'danger',
  cancelled: 'secondary',
  completed: 'info',
  on_hold: 'secondary'
};

const priorityColors: Record<string, string> = {
  low: 'success',
  medium: 'warning',
  high: 'danger'
};

interface AllRequestsTableProps {
  requests: AssetRequest[];
  searchQuery: string;
}

const AllRequestsTable: React.FC<AllRequestsTableProps> = ({
  requests,
  searchQuery
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { loading } = useSelector((state: RootState) => state.allRequests);
  const { actionLoading } = useSelector((state: RootState) => state.approvals);

  const [showActionModal, setShowActionModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<AssetRequest | null>(
    null
  );
  const [actionType, setActionType] = useState<'approve' | 'reject' | null>(
    null
  );
  const [loadingId, setLoadingId] = useState<number | null>(null);
  const [showPutOnHoldModal, setShowPutOnHoldModal] = useState(false);

  const filtered = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    if (!q) return requests;

    return requests.filter(
      request =>
        request.asset_name?.toLowerCase().includes(q) ||
        request.user_name?.toLowerCase().includes(q) ||
        request.status?.toLowerCase().includes(q) ||
        request.priority?.toLowerCase().includes(q) ||
        request.category_name?.toLowerCase().includes(q) ||
        // ADD company name to search
        request.company_name?.toLowerCase().includes(q)
    );
  }, [requests, searchQuery]);

  const handleApprove = (request: AssetRequest) => {
    setSelectedRequest(request);
    setActionType('approve');
    setShowActionModal(true);
  };

  const handleReject = (request: AssetRequest) => {
    setSelectedRequest(request);
    setActionType('reject');
    setShowActionModal(true);
  };

  const confirmAction = async () => {
    if (!selectedRequest || !actionType) return;

    setLoadingId(selectedRequest.id);
    try {
      if (actionType === 'approve') {
        await dispatch(approveRequest(selectedRequest.id)).unwrap();
      } else {
        await dispatch(rejectRequest(selectedRequest.id)).unwrap();
      }
      setShowActionModal(false);
      setSelectedRequest(null);
      setActionType(null);
    } catch (error) {
      console.error(`Failed to ${actionType} request:`, error);
    } finally {
      setLoadingId(null);
    }
  };

  const handlePutOnHold = (request: AssetRequest) => {
    setSelectedRequest(request);
    setShowPutOnHoldModal(true);
  };

  const handlePutOnHoldConfirm = async (reason: string) => {
    if (!selectedRequest) return;

    await dispatch(
      putOnHoldThunk({
        requestId: selectedRequest.id,
        payload: { hold_reason: reason }
      })
    ).unwrap();

    setShowPutOnHoldModal(false);
    setSelectedRequest(null);
  };

  const handleResume = async (request: AssetRequest) => {
    setLoadingId(request.id);
    try {
      await dispatch(resumeRequestThunk(request.id)).unwrap();
    } catch (error) {
      console.error('Failed to resume request:', error);
    } finally {
      setLoadingId(null);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const columns = useMemo<ColumnDef<AssetRequest>[]>(
    () => [
      {
        id: 'sn',
        header: 'SN',
        cell: ({ row }) => <span className="fw-semibold">{row.index + 1}</span>,
        meta: {
          headerProps: {
            style: { width: '80px' }
          },
          cellProps: {}
        }
      },
      {
        accessorKey: 'company_name',
        header: () => (
          <span>
            <FontAwesomeIcon icon={faBuilding} className="me-2 text-primary" />
            Company
          </span>
        ),
        cell: ({ getValue }) => (
          <span className="text-body">{(getValue() as string) || 'N/A'}</span>
        ),
        meta: {
          headerProps: {},
          cellProps: {}
        }
      },
      {
        accessorKey: 'asset_name',
        header: () => (
          <span>
            <FontAwesomeIcon icon={faLaptop} className="me-2 text-primary" />
            Asset
          </span>
        ),
        cell: ({ getValue }) => (
          <span className="text-body">{(getValue() as string) || 'N/A'}</span>
        ),
        meta: {
          headerProps: {},
          cellProps: {}
        }
      },
      {
        accessorKey: 'user_name',
        header: () => (
          <span>
            <FontAwesomeIcon icon={faUser} className="me-2 text-primary" />
            Requested By
          </span>
        ),
        cell: ({ getValue }) => (
          <span className="text-body">
            {(getValue() as string) || 'Unknown'}
          </span>
        ),
        meta: {
          headerProps: {},
          cellProps: {}
        }
      },
      {
        accessorKey: 'category_name',
        header: () => (
          <span>
            <FontAwesomeIcon icon={faTag} className="me-2 text-primary" />
            Category
          </span>
        ),
        cell: ({ getValue }) => (
          <span className="text-body">{(getValue() as string) || 'N/A'}</span>
        ),
        meta: {
          headerProps: {},
          cellProps: {}
        }
      },
      {
        accessorKey: 'priority',
        header: () => (
          <span>
            <FontAwesomeIcon
              icon={faExclamationCircle}
              className="me-2 text-primary"
            />
            Priority
          </span>
        ),
        cell: ({ getValue }) => {
          const priority = (getValue() as string)?.toLowerCase();

          const config: Record<
            string,
            { className: string; icon: string; label: string }
          > = {
            high: {
              className: 'badge-phoenix-danger',
              icon: 'arrow-up',
              label: 'High'
            },
            medium: {
              className: 'badge-phoenix-warning',
              icon: 'minus',
              label: 'Medium'
            },
            low: {
              className: 'badge-phoenix-success',
              icon: 'arrow-down',
              label: 'Low'
            }
          };

          const badge = config[priority] || {
            className: 'badge-phoenix-secondary',
            icon: 'help-circle',
            label: priority
          };

          return (
            <span className={`badge badge-phoenix fs-10 ${badge.className}`}>
              <span className="badge-label">{badge.label}</span>
              <FeatherIcon icon={badge.icon} size={12} className="ms-1" />
            </span>
          );
        },
        meta: {
          headerProps: {},
          cellProps: {}
        }
      },
      {
        accessorKey: 'status',
        header: () => (
          <span>
            <FontAwesomeIcon
              icon={faCheckCircle}
              className="me-2 text-primary"
            />
            Status
          </span>
        ),
        cell: ({ getValue }) => {
          const status = (getValue() as string)?.toLowerCase();

          // Map status to Phoenix badge color and professional icon
          const statusConfig: Record<
            string,
            { className: string; icon: string; label: string }
          > = {
            fulfilled: {
              className: 'badge-phoenix-success', // green
              icon: 'check-circle', // professional “completed” icon
              label: 'Fulfilled'
            },
            'on-hold': {
              className: 'badge-phoenix-warning', // yellow
              icon: 'pause-circle', // professional “on-hold” icon
              label: 'On Hold'
            },
            approved: {
              className: 'badge-phoenix-primary', // blue
              icon: 'thumbs-up', // professional “approved” icon
              label: 'Approved'
            },
            rejected: {
              className: 'badge-phoenix-danger', // red
              icon: 'x-circle', // professional “rejected” icon
              label: 'Rejected'
            },
            pending: {
              className: 'badge-phoenix-info', // cyan/neutral
              icon: 'clock', // professional “pending” icon
              label: 'Pending'
            }
          };

          const badge = statusConfig[status] || {
            className: 'badge-phoenix-secondary',
            icon: 'help-circle',
            label: status
          };

          return (
            <span className={`badge badge-phoenix fs-10 ${badge.className}`}>
              <span className="badge-label">{badge.label}</span>
              <FeatherIcon icon={badge.icon} size={12} className="ms-1" />
            </span>
          );
        },
        meta: {
          headerProps: {},
          cellProps: {}
        }
      },
      {
        accessorKey: 'created_at',
        header: () => (
          <span>
            <FontAwesomeIcon icon={faCalendar} className="me-2 text-primary" />
            Created At
          </span>
        ),
        cell: ({ getValue }) => (
          <span className="text-body">{formatDate(getValue() as string)}</span>
        ),
        meta: {
          headerProps: {},
          cellProps: {}
        }
      }
    ],
    [loadingId]
  );

  const table = useAdvanceTable({
    data: filtered,
    columns,
    pageSize: 10,
    pagination: true,
    sortable: true,
    selection: false
  });

  if (loading) {
    return <div className="text-center p-4">Loading requests...</div>;
  }

  return (
    <AdvanceTableProvider {...table}>
      <div
        className="bg-white"
        style={{
          borderRadius: '12px',
          border: '1px solid #e5e7eb'
        }}
      >
        <div className="table-responsive" style={{ overflowY: 'visible' }}>
          <AdvanceTable
            tableProps={{
              className: 'phoenix-table mb-0',
              hover: true
            }}
            headerClassName=""
            rowClassName="hover-actions-trigger btn-reveal-trigger align-middle"
          />
        </div>
        <div className="table-footer-elegant">
          <AdvanceTableFooter pagination className="px-3 py-2" />
        </div>
      </div>

      {/* Approve/Reject Confirmation Modal */}
      <Modal
        show={showActionModal}
        onHide={() => setShowActionModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {actionType === 'approve' ? 'Approve' : 'Reject'} Request
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to {actionType} this request for "
          {selectedRequest?.asset_name}"?
          {actionType === 'reject' && (
            <div className="mt-2 text-warning">
              <small>
                This action cannot be undone. The requester will be notified.
              </small>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowActionModal(false)}>
            Cancel
          </Button>
          <Button
            variant={actionType === 'approve' ? 'success' : 'danger'}
            onClick={confirmAction}
            disabled={loadingId !== null}
          >
            {loadingId !== null ? (
              <>
                <div className="d-flex align-items-center">
                  <PhoenixLoader />
                  <span className="ms-2">
                    {actionType === 'approve' ? 'Approving...' : 'Rejecting...'}
                  </span>
                </div>
              </>
            ) : (
              `Yes, ${actionType === 'approve' ? 'Approve' : 'Reject'}`
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Put On Hold Modal */}
      <PutOnHoldModal
        show={showPutOnHoldModal}
        handleClose={() => {
          setShowPutOnHoldModal(false);
          setSelectedRequest(null);
        }}
        request={selectedRequest}
        onPutOnHold={handlePutOnHoldConfirm}
      />
    </AdvanceTableProvider>
  );
};

export default AllRequestsTable;
